/**
 * useHostBridge - Host 宿主頁面的 postMessage 通信管理
 *
 * 通信協議：
 * - type: 'GAME_HOST' (固定標識)
 * - action: 消息動作類型
 * - payload: 消息數據
 *
 * 消息流向：
 * - OPEN_MODAL:   Cocos → Host  (游戲請求打開彈窗)
 * - MODAL_OPENED: Host → Cocos  (通知游戲彈窗已打開)
 * - MODAL_CLOSED: Host → Cocos  (通知游戲彈窗已關閉)
 */

// ==================== 類型定義 ====================

export type HostMessageAction =
  | 'OPEN_MODAL'
  | 'MODAL_OPENED'
  | 'MODAL_CLOSED'
  // Token 相關協議
  | 'SET_TOKEN' // Host → Cocos：下發 token
  | 'REQUEST_TOKEN' // Cocos → Host：請求 token
  | 'SET_LANGUAGE' // Host → Cocos：下發語言設置
  | 'REQUEST_LANGUAGE' // Cocos → Host：請求語言設置
  // 執行期設定協議
  | 'SET_CONFIG' // Host → Cocos：下發執行期設定（env / apiBaseUrl / externalModulesBaseDomain / gameCode）
  | 'REQUEST_CONFIG' // Cocos → Host：請求執行期設定
  | 'AUTH_INVALID' // Cocos → Host：token 失效（如 401）
  // 游戲狀態協議
  | 'GAME_READY' // Cocos → Host：引擎起來、listener 掛好（payload.hostLoading=true → 走新 loading 流程）
  // Loading 流程（2026-08，見 docs/LOADING_PROTOCOL.md；舊款不會送這些、host 也不會對舊款送 LOADING_CLOSED）
  | 'LOAD_COMPLETE' // Cocos → Host：資源載完、可以開始遊戲（新流程限定）
  | 'LOADING_CLOSED' // Host → Cocos：遮罩已卸、可以開始轉（走佇列、requiresAck；Cocos 端冪等）
  | 'LOAD_ERROR' // Cocos → Host：載入失敗 { code?, message? }
  | 'LOAD_ALIVE' // Cocos → Host：載入期心跳（每 5s，GAME_READY 後開始、LOAD_COMPLETE 時停）
  // 消息隊列協議
  | 'ACK' // Cocos → Host：消息回執
  | 'SPIN' // Host → Cocos：觸發 Spin 操作
  | 'EXIT_GAME' // Cocos → Host：游戲請求退出

export interface HostMessage {
  type: 'GAME_HOST'
  action: HostMessageAction
  payload: Record<string, unknown>
}

export interface ModalInstance {
  id: string
  key: string
  title: string
}

export type OpenModalHandler = (modalKey: string) => void
export type CloseModalHandler = (modalId?: string) => void

// ==================== 工具函數 ====================

export function createHostMessage(
  action: HostMessageAction,
  payload: Record<string, unknown>,
): HostMessage {
  return { type: 'GAME_HOST', action, payload }
}

export function generateModalId(): string {
  return `modal-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
}

export function isValidHostMessage(data: unknown): data is HostMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as HostMessage).type === 'GAME_HOST' &&
    typeof (data as HostMessage).action === 'string'
  )
}
